import { Button } from '@material-ui/core'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { EnumTipoSujeto } from '../../../api'
import Page from '../../../components/page/page'
import PageHeader from '../../../components/page/pageHeader'
import { enumEnumToValueNumber, enumToRecord, recordToMap } from '../../../helpers'
import { NonAuthRoutes } from '../../../model'
import {
  clearClientCommercials,
  clearClientContracts,
  clearClientData,
  storeActiveTabForCurrentPage,
  storeSearchText
} from '../../../redux'
import {
  selectClientData,
  selectIsFetchingClientData
} from '../../../redux/selectors/client.selectors'

import { UserPermission } from '@globaleris/ui.library'
import TabsWithPermissions, {
  Tab
} from '../../../components/tabsWithPermissions/tabsWithPermissions'
import { hasPermission, hasResource } from '../../../helpers/permissionsHelper'
import { usePageState } from '../../../hooks'
import { Resources } from '../../../model/RBAC/constants'
import { ClientFileInvoices } from './tabs/clientFileInvoices'
import { ClientFileMain } from './tabs/clientFileMain'
import { ClientFileTrades } from './tabs/clientFileTrades'

export interface ClientFileProps {
  location: {
    state: {
      clientID: number
      alternativeNavigationEnabled?: boolean
      fromCommunicationCenter?: boolean
    }
  }
  // commodities: EnumCommodity[] | undefined
}

enum CLIENT_FILE_TABS {
  MAIN = 0,
  INVOICES = 1,
  TRADES = 2
}

export const ClientFile: React.FC<ClientFileProps> = props => {
  const [t] = useTranslation([
    'globalerisLibrary',
    'clients',
    'clientFile',
    'enums',
    'comunicationProcedures',
    'procedures'
  ])
  const history = useHistory()

  const clientID = props.location?.state?.clientID
  const clientData = useSelector(selectClientData)
  const isFetchingClient = useSelector(selectIsFetchingClientData)
  const alternativeNavigationEnabled = props.location?.state?.alternativeNavigationEnabled ?? false
  const fromCommunicationCenter = props.location?.state?.fromCommunicationCenter ?? false

  const { activeTab } = usePageState()
  const [currentTab, setCurrentTab] = useState<number>(0)

  const handleClickBackButton = () => {
    clearClientData()
    clearClientContracts()
    clearClientCommercials()
    history.push(NonAuthRoutes.ClientSearch, {
      subjectType: enumEnumToValueNumber(EnumTipoSujeto, clientData?.type)
    })
  }

  const handleClickExitButton = () => {
    storeSearchText('')
    clearClientData()
    clearClientContracts()
    clearClientCommercials()
    if (alternativeNavigationEnabled) {
      history.goBack()
      return
    }
    history.push(NonAuthRoutes.Client, {
      subjectType: enumEnumToValueNumber(EnumTipoSujeto, clientData?.type)
    })
  }

  const renderTabContent = (activeTab: number) => {
    const clientIDFallback = clientID ?? clientData?.id
    switch (permittedTabs[activeTab]?.id) {
      case CLIENT_FILE_TABS.TRADES:
        return <ClientFileTrades clientID={clientIDFallback} />
      case CLIENT_FILE_TABS.INVOICES:
        return <ClientFileInvoices clientID={clientIDFallback} />
      default:
        return <ClientFileMain clientID={clientIDFallback} />
    }
  }

  const tabs = enumToRecord(CLIENT_FILE_TABS)
  const hasClientPermission = hasPermission(Resources.Client, [
    UserPermission.EDIT,
    UserPermission.READ_ONLY
  ])
  const hasExtendedClientPermission = hasPermission(Resources.ExtendedClients, [
    UserPermission.EDIT,
    UserPermission.READ_ONLY
  ])
  const hasCommercialResource = hasResource(Resources.Commercial)
  const hasAdministrationResource = hasResource(Resources.Administration)
  const hasInvoicesResource = hasResource(Resources.InvoicesPage)

  const tabPermissionMapper: Record<number, () => boolean> = {
    [CLIENT_FILE_TABS.MAIN]: () =>
      hasClientPermission ||
      hasExtendedClientPermission ||
      hasCommercialResource ||
      hasAdministrationResource, //TODO recurso de mayorista??,
    [CLIENT_FILE_TABS.INVOICES]: () => hasInvoicesResource,
    [CLIENT_FILE_TABS.TRADES]: () =>
      enumEnumToValueNumber(EnumTipoSujeto, clientData?.type) === EnumTipoSujeto.ClienteExtendido ||
      enumEnumToValueNumber(EnumTipoSujeto, clientData?.type) === EnumTipoSujeto.Mayorista
  }

  // The tabs component indexes only the tabs it renders (those with permission), so we keep only
  // the permitted ones to make the tab index match the tab id.
  const permittedTabs: Tab[] = Object.keys(tabs)
    .map(typeName => {
      const tabId = tabs[typeName]

      return {
        id: tabId,
        label: t(`clientFile:tabs.${typeName.toLowerCase()}`),
        hasPermission: tabPermissionMapper[tabId] ? tabPermissionMapper[tabId]() : false
      }
    })
    .filter(tab => tab.hasPermission)

  const tabsDefinition: Record<number, Tab> = permittedTabs.reduce((acc, tab, index) => {
    acc[index] = tab

    return acc
  }, {})

  const onTabChangeHandler = (tabIdx: number) => {
    if (currentTab !== tabIdx) {
      setCurrentTab(tabIdx)
      storeActiveTabForCurrentPage(tabIdx)
    }
  }

  useEffect(() => {
    if (activeTab !== undefined) {
      setCurrentTab(activeTab)
    } else {
      storeActiveTabForCurrentPage(0)
    }
  }, [activeTab])

  return (
    <Page fullWidth={true}>
      <PageHeader title={t('clientFile:title')}>
        <Button
          variant="text"
          color="primary"
          disabled={isFetchingClient}
          onClick={handleClickExitButton}
        >
          {t('clients:searchPage.buttons.exit')}
        </Button>
        {!fromCommunicationCenter && (
          <Button
            variant="text"
            color="primary"
            disabled={isFetchingClient}
            onClick={handleClickBackButton}
          >
            {t('clients:searchPage.buttons.backToSearch')}
          </Button>
        )}
      </PageHeader>
      <TabsWithPermissions
        tabs={recordToMap(tabsDefinition)}
        tabDefault={currentTab}
        onTabChange={onTabChangeHandler}
      ></TabsWithPermissions>
      {renderTabContent(activeTab ? activeTab : currentTab)}
    </Page>
  )
}
